"use client";

import { useEffect, useState, useRef, useCallback, useMemo } from "react";

// Fixed delivery windows per session (compact, shown next to the cutoff time)
const DELIVERY_WINDOWS = {
  Breakfast: "7-8 AM",
  Lunch: "12-1 PM",
  Dinner: "7-8 PM",
};

const fmtTime12h = (d) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function CutoffStatusCard({
  cutoffValidation,
  userStatus,
  currentSelectedDate,
  selectedSession,
  cutoffLoading,
  isInWindow = false,
  offer = null, // acquisition offer (mealsRemaining, offerCap, campaignEndDate…) or null
}) {
  const [timeLeft, setTimeLeft] = useState("");
  const [showOfferPanel, setShowOfferPanel] = useState(false);
  const intervalRef = useRef(null);

  // Offer panel auto-hides on any page scroll
  useEffect(() => {
    if (!showOfferPanel) return;
    const hide = () => setShowOfferPanel(false);
    window.addEventListener("scroll", hide, { passive: true });
    return () => window.removeEventListener("scroll", hide);
  }, [showOfferPanel]);

  const isEmployee = userStatus === "Employee";
  const orderMode = cutoffValidation?.orderMode || "preorder";

  const formatTimeLeft = useCallback((milliseconds) => {
    if (milliseconds <= 0) return "Cutoff passed";
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    if (minutes > 0) return `${minutes}m ${seconds}s left`;
    return `${seconds}s left`;
  }, []);

  // Stable cutoff timestamp — only changes when the actual cutoff time value changes
  const cutoffTimestamp = cutoffValidation?.cutoffDateTime
    ? new Date(cutoffValidation.cutoffDateTime).getTime()
    : null;

  // Trust the API's allowed flag — the backend performs the authoritative cutoff check.
  // Local clock comparison was causing false negatives on IST dev machines where the
  // corrected cutoffDateTime ends up shifted 5.5h early.
  const isActuallyAllowed = cutoffValidation?.allowed ?? true;

  // Memoize display info so it only recalculates when cutoff time or selected date actually changes
  const displayInfo = useMemo(() => {
    if (!cutoffValidation?.cutoffDateTime) return null;

    const cutoffDate = new Date(cutoffValidation.cutoffDateTime);
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    const getDeliveryDateLabel = () => {
      if (!currentSelectedDate) {
        if (isEmployee || orderMode === "instant") return { isToday: true };
        return { isToday: false };
      }
      const todayLocal = new Date();
      const todayStr = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth() + 1).padStart(2, "0")}-${String(todayLocal.getDate()).padStart(2, "0")}`;
      const selStr =
        typeof currentSelectedDate === "string"
          ? currentSelectedDate.slice(0, 10)
          : `${new Date(currentSelectedDate).getFullYear()}-${String(new Date(currentSelectedDate).getMonth() + 1).padStart(2, "0")}-${String(new Date(currentSelectedDate).getDate()).padStart(2, "0")}`;
      const [y, m, d] = selStr.split("-").map(Number);
      const selDate = new Date(y, m - 1, d);
      return { isToday: todayStr === selStr, date: selDate };
    };

    const { isToday, date: deliveryDateObj } = getDeliveryDateLabel();
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isSameDay = (a, b) =>
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear();

    // "Today" / "Tomorrow", or the weekday name for anything further out
    const getRelativeLabel = (date) => {
      if (isSameDay(date, today)) return "Today";
      if (isSameDay(date, tomorrow)) return "Tomorrow";
      return weekdays[date.getDay()];
    };

    const allowed = isActuallyAllowed;
    // During the post-cutoff window, ordering is still for today — don't shift to tomorrow.
    const effectiveDeliveryDateObj =
      !allowed && isToday && !isInWindow ? tomorrow : deliveryDateObj;

    let displayDate;

    if (isEmployee) {
      displayDate = effectiveDeliveryDateObj || today;
    } else if (orderMode === "instant") {
      displayDate = effectiveDeliveryDateObj || today;
    } else {
      displayDate = effectiveDeliveryDateObj || tomorrow;
    }

    const dayLabel = getRelativeLabel(displayDate);

    return { dayLabel, cutoffDate, allowed };
  }, [
    cutoffTimestamp,
    cutoffValidation?.allowed,
    isEmployee,
    orderMode,
    currentSelectedDate,
    isInWindow,
  ]);

  // Countdown interval — restarts when cutoff time changes
  // Only ticks when within 3 hours of cutoff
  const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
  useEffect(() => {
    if (!displayInfo?.cutoffDate) return;
    const updateTimer = () => {
      const now = new Date();
      const msLeft = displayInfo.cutoffDate - now;
      // Only show time when within 3 hours of cutoff (or already past)
      if (msLeft <= THREE_HOURS_MS) {
        setTimeLeft(formatTimeLeft(msLeft));
      } else {
        setTimeLeft(""); // hide pill when more than 3 hours away
      }
    };
    updateTimer();
    intervalRef.current = setInterval(updateTimer, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [displayInfo?.cutoffDate, formatTimeLeft]);

  // Show skeleton while loading or before first data arrives
  const showSkeleton =
    cutoffLoading ||
    (!displayInfo &&
      (cutoffValidation === null || cutoffValidation === undefined));

  if (showSkeleton) {
    return null; // Home.jsx gates rendering until data is ready — no skeleton needed
  }

  if (!displayInfo) return null;

  const { dayLabel, cutoffDate, allowed } = displayInfo;

  // Session label ("Breakfast" / "Lunch" / "Dinner") + its delivery window
  const sessionLabel = selectedSession
    ? selectedSession.charAt(0).toUpperCase() +
      selectedSession.slice(1).toLowerCase()
    : "";
  const deliveryWindow = DELIVERY_WINDOWS[sessionLabel] || null;

  // Hide only when the API confirms ordering is not allowed AND no window is active.
  if (!allowed && !isInWindow) return null;

  // Only show the time pill when within 3 hours of cutoff (timeLeft is non-empty)
  const showTimePill = timeLeft !== "";

  const mealsLeft = offer?.mealsRemaining || 0;
  const offerUsedToday = Boolean(offer?.usedToday && !offer?.eligible);
  // Whole days remaining in the campaign (at least 1 while the offer is still live).
  const daysLeft = offer?.campaignEndDate
    ? Math.max(
        1,
        Math.ceil((new Date(offer.campaignEndDate) - new Date()) / 86400000),
      )
    : null;

  return (
    <div className="cutoff-status-main-card">
      <div className="cutoff-status-card">
        <div className="cutoff-inner">
          <div className="cutoff-info-group">
            <div className="cutoff-text-group">
              <span className="cutoff-label">Ordering for</span>
              <span className="cutoff-date">
                {dayLabel}
                {sessionLabel ? `, ${sessionLabel}` : ""}
              </span>
              <span className="cutoff-meta">
                Order by {fmtTime12h(cutoffDate)}
                {deliveryWindow ? ` · Delivered ${deliveryWindow}` : ""}
              </span>
            </div>
          </div>
          {/* Time pill — hidden in UI for now; logic preserved above */}
          {/* {showTimePill && <div className="time-left-pill">{timeLeft}</div>} */}
          {offer && mealsLeft > 0 && (
            <button
              className={`offer-gift-btn ${showOfferPanel ? "armed" : "gift-pulse"}`}
              onClick={() => setShowOfferPanel((v) => !v)}
              aria-label={`${mealsLeft} free meals left`}
            >
              <span className="offer-gift-emoji">🎁</span>
              <span className="offer-gift-badge">{mealsLeft}</span>
            </button>
          )}
        </div>

        {/* Offer dropdown panel — anchored below the strip, right side.
            Toggles on gift-icon tap; auto-hides on scroll. */}
        {offer && mealsLeft > 0 && (
          <div
            className={`offer-reward-panel ${showOfferPanel ? "show" : ""}`}
          >
            <div className="offer-panel-title">
              🎁 {mealsLeft} free meal{mealsLeft !== 1 ? "s" : ""} left
            </div>
            <div className="offer-panel-text">
              {[
                offerUsedToday
                  ? "Used today — come back tomorrow"
                  : offer.offerCap
                    ? `₹${offer.offerCap} off`
                    : null,
                daysLeft != null
                  ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        )}

        <style jsx>{`
  .cutoff-status-main-card {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Inter", sans-serif;
    width: 100%;
    max-width: 100vw;
    /* overflow stays visible so the offer dropdown panel isn't clipped */
    box-sizing: border-box;
  }
  .cutoff-status-card {
    max-width: 655px;
    width: 100%;
    background: #e6b800;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    /* visible (not hidden): the offer panel drops below the card edge */
    overflow: visible;
    position: relative;
    box-sizing: border-box;
  }
  .cutoff-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    width: 100%;
    gap: 16px;
  }
  .cutoff-info-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }
  .cutoff-text-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .cutoff-label {
    font-size: 13px;
    font-weight: 400;
    // color: rgba(139, 69, 19, 1);
    color: #2c2c2c;
    line-height: 1.3;
    letter-spacing: -0.1px;
  }
  .cutoff-date {
    font-size: 20px;
    font-weight: 700;
    // color: #8b4513;
    color: #2c2c2c;
    line-height: 1.25;
    letter-spacing: -0.4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cutoff-meta {
    font-size: 12px;
    font-weight: 500;
    color: #2c2c2c;
    line-height: 1.35;
    letter-spacing: -0.1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .time-left-pill {
    border-radius: 40px;
    padding: 6px 16px;
    font-weight: 600;
    font-size: 14px;
    color: #4a2a0c;
    background-color: #fdf2d0;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.3),
      0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .offer-gift-btn {
    position: relative;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    transition: transform 0.15s ease;
  }
  .offer-gift-btn.armed {
    transform: scale(1.08);
  }
  .offer-gift-btn.gift-pulse {
    animation: giftShake 2.6s infinite;
  }
  @keyframes giftShake {
    0%, 88%, 100% { transform: rotate(0deg); }
    90% { transform: rotate(-12deg); }
    92% { transform: rotate(10deg); }
    94% { transform: rotate(-8deg); }
    96% { transform: rotate(6deg); }
    98% { transform: rotate(-3deg); }
  }
  .offer-gift-emoji {
    font-size: 22px;
    line-height: 1;
  }
  .offer-gift-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #3d4b16;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    min-width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #e6b800;
    box-sizing: content-box;
    padding: 0 1px;
  }
  .offer-reward-panel {
    position: absolute;
    top: 100%;
    right: 16px;
    width: 232px;
    margin-top: 8px;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    padding: 0 14px;
    transition:
      max-height 0.28s ease,
      opacity 0.22s ease,
      padding 0.28s ease;
    background: #ffffff;
    border: 1px solid #eee0c8;
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
    z-index: 30;
  }
  .offer-reward-panel.show {
    max-height: 110px;
    opacity: 1;
    padding: 12px 14px;
  }
  .offer-panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #2c2c18;
  }
  .offer-panel-text {
    font-size: 11px;
    color: #6b6548;
    line-height: 1.5;
    margin-top: 4px;
  }

  /* ✅ Mobile & Tablets (below 1024px) - FULL WIDTH (MUST come first) */
  @media (max-width: 1023px) {
    .cutoff-status-card {
      max-width: 100% !important;
      width: 100%;
      margin: 0;
      border-radius: 0 0 16px 16px;
    }
    .cutoff-inner {
      padding: 10px 16px;
      gap: 10px;
    }
    .cutoff-info-group {
      min-width: 0;
      flex: 1;
    }
    .cutoff-date {
      font-size: 16px;
    }
    .cutoff-label {
      font-size: 11px;
    }
    .cutoff-meta {
      font-size: 10.5px;
    }
    .offer-gift-btn {
      width: 34px;
      height: 34px;
    }
    .time-left-pill {
      font-size: 11px;
      padding: 4px 10px;
    }
  }

  /* ✅ Laptop-S (1280×800) → 406px centered */
  @media (min-width: 1024px) and (max-width: 1300px) {
    .cutoff-status-card {
      max-width: 406px;
    }
    .cutoff-inner {
      padding: 10px 14px;
      gap: 12px;
    }
    .cutoff-date {
      font-size: 16px;
    }
    .cutoff-label {
      font-size: 11px;
    }
    .cutoff-meta {
      font-size: 10.5px;
    }
    .time-left-pill {
      font-size: 11px;
      padding: 4px 10px;
    }
  }

  /* ✅ Laptop-M (1366×768) → 435px centered */
  @media (min-width: 1301px) and (max-width: 1450px) {
    .cutoff-status-card {
      max-width: 435px;
    }
    .cutoff-inner {
      padding: 11px 16px;
      gap: 14px;
    }
    .cutoff-date {
      font-size: 17px;
    }
    .cutoff-label {
      font-size: 12px;
    }
    .cutoff-meta {
      font-size: 11px;
    }
  }

  /* ✅ Desktop (1920px and above) → 655px centered */
  @media (min-width: 1451px) {
    .cutoff-status-card {
      max-width: 655px;
    }
  }

  /* ✅ Very small phones (below 480px) - extra compact */
  @media (max-width: 480px) {
    .cutoff-inner {
      padding: 8px 12px;
      gap: 8px;
    }
    .cutoff-date {
      font-size: 18px;
    }
    .cutoff-label {
      font-size: 16px;
    }
    .cutoff-meta {
      font-size: 14px;
    }
    .offer-gift-btn {
      width: 32px;
      height: 32px;
    }
    .offer-gift-emoji {
      font-size: 20px;
    }
    .time-left-pill {
      font-size: 10px;
      padding: 3px 8px;
    }
  }
`}</style>
      </div>
    </div>
  );
}












